/* global routes, publish, PUB_SUB_EVENTS */

(function () {
  if (customElements.get('cart-protection-picker')) return;

  // ─────────────────────────────────────────────────────────────────────
  // Cleanup of stray protection lines.
  //
  // The cart UI hides protection line items (main-cart-items.liquid /
  // cart-drawer.liquid skip rows where _protection_for_id is set), so the
  // ONLY way customers can see / change them is via the picker. If the
  // cart somehow ends up with:
  //   • orphan protections (whose _protection_for_id no longer matches any
  //     visible host's variant id), or
  //   • duplicate protections (more than one for the same host),
  // the customer can be billed for invisible items and the picker can fall
  // out of sync with the order summary. This routine removes both kinds.
  // ─────────────────────────────────────────────────────────────────────
  function cleanupOrphanProtections(cartData) {
    // Never make orphan/delete decisions from event.cartData. /cart/add.js
    // can return only the just-added line item(s), so a protection-only add
    // response does not prove its host product is missing from the cart.
    if (
      cartData &&
      Array.isArray(cartData.items) &&
      cartData.items.length === 1 &&
      cartData.items[0].properties &&
      cartData.items[0].properties._protection_for_id
    ) {
      return Promise.resolve(null);
    }

    return fetchCartItems().then(cleanupOrphanProtectionsFromCart);
  }

  function cleanupOrphanProtectionsFromCart(cartData) {
    if (!cartData || !Array.isArray(cartData.items)) return Promise.resolve(null);

    var hostVariantIds = {};
    cartData.items.forEach(function (it) {
      var isProtection = it.properties && it.properties._protection_for_id;
      if (!isProtection) {
        hostVariantIds[String(it.variant_id)] = true;
      }
    });

    var orphanKeys = [];
    var perHost = {};
    cartData.items.forEach(function (it) {
      if (it.properties && it.properties._protection_for_id) {
        var pId = String(it.properties._protection_for_id);
        if (!hostVariantIds[pId]) {
          orphanKeys.push(it.key);
        } else {
          (perHost[pId] = perHost[pId] || []).push(it.key);
        }
      }
    });

    // Duplicates: same host with multiple protection lines — keep the
    // LAST, drop the earlier ones. (Last in cart.items === most recent
    // insertion === closest to the user's most recent picker action,
    // which is almost always the right one to preserve.)
    //
    // Earlier this used keys.slice(1), keeping the FIRST. That was
    // catastrophic when stale-read race made cleanup see a duplicate
    // right after the user picked a new plan: cleanup would delete the
    // user's just-added line, producing the user-reported "selected,
    // then auto-cancelled" symptom.
    var duplicateKeys = [];
    Object.keys(perHost).forEach(function (hostId) {
      var keys = perHost[hostId];
      if (keys.length > 1) duplicateKeys = duplicateKeys.concat(keys.slice(0, -1));
    });

    var allToDelete = orphanKeys.concat(duplicateKeys);
    if (allToDelete.length === 0) return Promise.resolve(null);

    return bulkRemoveLines(allToDelete).then(function (newCart) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        // Publish with source 'cart-items' so Dawn's CartItems.onCartUpdate
        // doesn't fire its stale-prone refetch. The cleanup didn't update
        // any visible cart line items (it removed invisible orphans), so
        // there's no DOM update needed beyond what other subscribers do.
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: newCart,
        });
      }
      return newCart;
    }).catch(function (e) {
      console.error('[protection-orphan-cleanup]', e);
    });
  }

  function fetchCartItems() {
    var cartUrl = (typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : '/cart') + '.js';
    // cache: 'no-store' — refuse browser/CDN cached responses. Without
    // it, a stale cart could mislead pre-clean into thinking everything
    // is already clean, miss a leftover protection line, and let
    // /cart/add.js stack a duplicate on top.
    return fetch(cartUrl, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then(function (r) {
      return r.json().then(function (cartData) {
        if (!r.ok || !cartData || cartData.status || !Array.isArray(cartData.items)) {
          throw new Error('Unable to verify the current cart state');
        }
        return cartData;
      });
    });
  }

  function verifyHostProtectionRemoval(cartData, hostVariantId) {
    if (!cartData || !Array.isArray(cartData.items)) {
      throw new Error('Protection cleanup returned an unverifiable cart response');
    }

    var hasRemainingProtection = cartData.items.some(function (item) {
      return item.properties && String(item.properties._protection_for_id) === String(hostVariantId);
    });

    if (hasRemainingProtection) {
      throw new Error('Protection cleanup left an existing plan for this product');
    }

    return cartData;
  }

  function pickerHasSelectedProtection(picker) {
    return !!picker.querySelector(
      '.cart-protection-picker__checkbox[data-action="add"]:checked, ' +
      '.cart-protection-picker__checkbox[data-action="remove"]:checked'
    );
  }

  function syncPickerSelection(picker, protectionLine) {
    var inputs = picker.querySelectorAll('.cart-protection-picker__checkbox');
    var selectedInput = null;
    var declineInput = picker.querySelector('.cart-protection-picker__checkbox[value="decline"]');
    var targetVariantId = protectionLine && protectionLine.variant_id
      ? String(protectionLine.variant_id)
      : '';

    if (declineInput) {
      if (protectionLine && protectionLine.key) {
        declineInput.dataset.action = 'remove';
        declineInput.dataset.lineKey = protectionLine.key;
      } else {
        declineInput.dataset.action = 'none';
        delete declineInput.dataset.lineKey;
      }
    }

    inputs.forEach(function (input) {
      input.checked = false;
      input.removeAttribute('data-current');

      if (targetVariantId && input.dataset.action === 'add' && input.dataset.variantId === targetVariantId) {
        selectedInput = input;
      }
    });

    if (protectionLine && !selectedInput) {
      selectedInput = picker.querySelector(
        '.cart-protection-picker__checkbox[data-action="remove"]'
      );
    }

    if (!protectionLine) {
      selectedInput = picker.querySelector('.cart-protection-picker__checkbox[value="decline"]');
    }

    if (selectedInput) {
      selectedInput.checked = true;
      selectedInput.setAttribute('data-current', 'true');
    }
  }

  // Remove one or more cart lines. Tries /cart/update.js first; falls back
  // to per-line /cart/change.js if Shopify 422s (case-pack / min-order
  // rules sometimes reject quantity:0 on the bulk endpoint). Throws when
  // every strategy fails for every key — callers must handle.
  function bulkRemoveLines(lineKeys) {
    if (!lineKeys || lineKeys.length === 0) return Promise.resolve(null);

    var updateUrl = (typeof routes !== 'undefined' && routes.cart_update_url
      ? routes.cart_update_url
      : '/cart/update') + '.js';

    var updates = {};
    lineKeys.forEach(function (k) { updates[k] = 0; });

    return fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ updates: updates }),
    })
      .then(function (r) {
        return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; });
      })
      .then(function (res) {
        if (res.ok && !(res.body && res.body.status)) return res.body;
        console.warn('[bulkRemoveLines] /cart/update.js rejected (' + res.status + '), falling back to per-line /cart/change.js', res.body);
        return removeLinesIndividually(lineKeys);
      });
  }

  // Per-line /cart/change.js fallback. Walks the keys sequentially
  // (Shopify only commits one cart mutation at a time anyway). Returns
  // the authoritative cart from the LAST successful change. Partial
  // success can still return normally for best-effort orphan cleanup;
  // charge-creating callers MUST verify their host has no protection left
  // before adding a replacement. Throws when every key failed.
  function removeLinesIndividually(lineKeys) {
    var changeUrl = (typeof routes !== 'undefined' && routes.cart_change_url
      ? routes.cart_change_url
      : '/cart/change') + '.js';

    var lastCart = null;
    var failures = [];

    return lineKeys.reduce(function (chain, key) {
      return chain.then(function () {
        return fetch(changeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ id: key, quantity: 0 }),
        })
          .then(function (r) {
            return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; });
          })
          .then(function (res) {
            if (res.ok && !(res.body && res.body.status)) {
              lastCart = res.body;
            } else {
              failures.push({ key: key, status: res.status, body: res.body });
              console.error('[removeLinesIndividually] /cart/change.js failed for key', key, res.body);
            }
          })
          .catch(function (e) {
            failures.push({ key: key, error: e });
            console.error('[removeLinesIndividually] /cart/change.js threw for key', key, e);
          });
      });
    }, Promise.resolve()).then(function () {
      if (failures.length === lineKeys.length) {
        throw new Error('Unable to remove ' + lineKeys.length + ' protection line(s); server rejected every strategy');
      }
      return lastCart;
    });
  }

  // NOTE: an earlier version subscribed to PUB_SUB_EVENTS.cartUpdate and
  // ran cleanupOrphanProtections on every cart change. That created a
  // race trap: when the user picked a plan, applyCartUpdate published
  // 'cart-items' with the add response, the subscriber fired, and if
  // the response (or the read replica /cart.js) showed the cart with
  // both the old plan AND the just-added plan (because pre-clean's
  // delete hadn't propagated to the read view yet), cleanup classified
  // the new plan as a duplicate and tried to delete it. User-reported
  // symptom: "选了之后被取消选择 / picker reverts moments after add".
  //
  // The subscriber is removed. Cleanup still runs in two safe contexts:
  //   • Pre-clean inside addPlan — atomic switch, runs BEFORE the add
  //     and uses cache:no-store so it sees a fresh cart.
  //   • cleanupOnPageLoad — page navigation, no recently-committed
  //     write to race against.
  // cart-protection-sync.js separately handles host-removal cleanup
  // (when the user deletes a main item, its protection follows).

  // Page-load cleanup: catches stale state from earlier sessions /
  // variant swaps / any flow that didn't fire a cartUpdate event.
  function cleanupOnPageLoad() {
    // The drawer shell loads this file so a picker injected into an initially
    // empty drawer upgrades automatically. Registration still completes in
    // this script evaluation; an empty drawer has no protection state to reconcile,
    // so skip the otherwise unconditional /cart.js request. Once AJAX injects
    // <cart-protection-picker>, the browser invokes connectedCallback itself.
    var emptyDrawer = document.querySelector('cart-drawer.is-empty');
    var renderedPicker = document.querySelector('cart-protection-picker');
    if (emptyDrawer && !renderedPicker) return;

    const cleanup = async () => {
      const updated = await cleanupOrphanProtections();
      if (updated && window.CartOperations) await window.CartOperations.refresh();
    };
    if (window.CartOperations) window.CartOperations.run(() => cleanup().catch(() => window.CartOperations.recover()));
    else cleanup().catch(function () {});
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanupOnPageLoad);
  } else {
    cleanupOnPageLoad();
  }

  class CartProtectionPicker extends HTMLElement {
    connectedCallback() {
      this.busy = false;
      this.bindAll();
    }

    bindAll() {
      this.querySelectorAll('.cart-protection-picker__checkbox').forEach((cb) => {
        cb.addEventListener('change', (e) => this.onChange(e));
      });
    }

    onChange(event) {
      // Stop bubbling so Dawn's CartItems debouncedOnChange (registered on
      // the parent <cart-items>) doesn't try to validateQuantity our
      // checkbox. Without this stop, a checkbox change can be picked up
      // by Dawn's quantity input handler, which has parsed-number / NaN
      // edge cases that have caused weird re-render races.
      event.stopPropagation();

      if (this.busy) {
        // Snap back to previous state if a request is already in flight
        this.revertCheckbox(event.target);
        return;
      }
      var input = event.target;
      var action = input.dataset.action;
      this.clearPickerError();

      if (action === 'add') {
        // Radio-like enforcement: uncheck siblings immediately so the user
        // never sees two plans selected at once during the AJAX round-trip.
        this.querySelectorAll('.cart-protection-picker__checkbox[data-action="add"]').forEach(function (cb) {
          if (cb !== input) cb.checked = false;
        });
        if (window.CartOperations) window.CartOperations.run(() => this.addPlan(input));
        else this.addPlan(input);
      } else if (action === 'remove') {
        if (window.CartOperations) window.CartOperations.run(() => this.removePlan(input));
        else this.removePlan(input);
      } else if (action === 'none') {
        input.checked = true;
      }
    }

    revertCheckbox(input) {
      if (!input) return;

      if (input.type === 'radio') {
        this.querySelectorAll('.cart-protection-picker__checkbox').forEach(function (radio) {
          radio.checked = radio.dataset.current === 'true';
        });
        return;
      }

      // Cart-page legacy picker: revert only the clicked checkbox.
      input.checked = !input.checked;
    }

    showPickerError(message) {
      const box = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (box) box.textContent = message || window.cartStrings?.error || 'Could not update your protection plan. Please try again.';
    }

    clearPickerError() {
      const box = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (box) box.textContent = '';
    }

    addPlan(input) {
      var variantId = parseInt(input.dataset.variantId, 10);
      if (!variantId) return;

      var hostVariantId = this.dataset.hostVariantId || '';
      if (!hostVariantId) {
        console.error('[cart-protection-picker] missing hostVariantId; aborting');
        this.revertCheckbox(input);
        return;
      }

      var hostQty = parseInt(this.dataset.hostQuantity || '1', 10) || 1;
      var planLabel = input.dataset.planLabel || 'Protection Plan';
      var planValue = input.dataset.planValue || '';
      var coverageNote = this.dataset.coverageNote || '';

      var properties = {
        _protection_for_id: hostVariantId,
        _protection_plan_label: planLabel,
        _protection_plan_value: planValue,
      };
      if (coverageNote) {
        properties._protection_coverage_note = coverageNote;
      }

      var self = this;
      var addBody = { items: [{ id: variantId, quantity: hostQty, properties: properties }] };

      // PRE-CLEAN: before adding, remove anything that would conflict with
      // the new plan. This is a SUPERSET of "atomic switch":
      //   1. Any protection whose _protection_for_id matches THIS host
      //      (atomic switch — the user is replacing the current plan)
      //   2. Any protection whose host doesn't exist anymore
      //      (orphan from variant swap or earlier session)
      //   3. Duplicate protections for any host
      // After delete, do the add. This removes the entire class of bugs
      // where the picker re-renders to State B because a stray protection
      // exists with the wrong _protection_for_id.
      this.setBusy(true);
      return fetchCartItems()
        .then(function (cartData) {
          var keysToDelete = [];
          var currentHostKeys = [];
          var hostVariantIds = {};
          cartData.items.forEach(function (it) {
            var isProtection = it.properties && it.properties._protection_for_id;
            if (!isProtection) hostVariantIds[String(it.variant_id)] = true;
          });
          var seenPerHost = {};
          cartData.items.forEach(function (it) {
            if (!it.properties || !it.properties._protection_for_id) return;
            var pId = String(it.properties._protection_for_id);
            // Replace any existing plan for this host
            if (pId === hostVariantId) {
              if (!it.key) throw new Error('Existing protection plan is missing its line key');
              currentHostKeys.push(it.key);
              keysToDelete.push(it.key);
              return;
            }
            // Drop orphans
            if (!hostVariantIds[pId]) { keysToDelete.push(it.key); return; }
            // Drop duplicates per host
            if (seenPerHost[pId]) { keysToDelete.push(it.key); return; }
            seenPerHost[pId] = true;
          });
          return bulkRemoveLines(keysToDelete).then(function (cleanedCart) {
            if (currentHostKeys.length === 0) return cleanedCart;
            return verifyHostProtectionRemoval(cleanedCart, hostVariantId);
          });
        })
        .then(function () {
          // Now add the new plan and let runRequest handle DOM updates.
          self.setBusy(false); // runRequest will re-set busy
          return self.runRequest(self.cartAddUrl(), addBody, input);
        })
        .catch(function (err) {
          console.error('[cart-protection-picker.addPlan]', err);
          self.setBusy(false);
          self.revertCheckbox(input);
          self.showPickerError();
          if (window.CartOperations) return window.CartOperations.recover();
        });
    }

    removePlan(input) {
      var lineKey = input.dataset.lineKey || '';
      if (!lineKey) return;
      return this.tryRemoveWithFallback(lineKey, input);
    }

    tryRemoveWithFallback(lineKey, input) {
      var self = this;
      this.setBusy(true);

      var cart = this.findCartHost();
      var sections = this.sectionsForCart(cart);
      var commonPayload = { sections: sections, sections_url: window.location.pathname };

      function doFetch(url, body) {
        return fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(Object.assign({}, body, commonPayload)),
        }).then(function (r) {
          return r.json().then(function (j) { return { ok: r.ok, body: j, status: r.status }; });
        });
      }

      function isShopifyError(res) {
        return !res.ok || (res.body && res.body.status);
      }

      // Strategy 1: /cart/update.js with key-quantity object
      var updates = {};
      updates[lineKey] = 0;

      return doFetch(self.cartUpdateUrl(), { updates: updates })
        .then(function (res1) {
          if (!isShopifyError(res1)) {
            self.applyCartUpdate(res1.body, cart);
            return null;
          }
          // Strategy 2: /cart/change.js with id (line item key)
          console.warn('[picker.removePlan] /cart/update.js failed (' + res1.status + ')', res1.body, '— trying /cart/change.js with id', lineKey);
          return doFetch(self.cartChangeUrl(), { id: lineKey, quantity: 0 }).then(function (res2) {
            if (!isShopifyError(res2)) {
              self.applyCartUpdate(res2.body, cart);
              return null;
            }
            // Both strategies failed. Bundle both error bodies so the
            // verify step below has full diagnostic context.
            var err = new Error('Both /cart/update.js and /cart/change.js rejected the removal');
            err.update_status = res1.status;
            err.update_body = res1.body;
            err.change_status = res2.status;
            err.change_body = res2.body;
            throw err;
          });
        })
        .catch(function (err) {
          console.error('[picker.removePlan] both strategies failed', {
            lineKey: lineKey,
            update: { status: err.update_status, body: err.update_body },
            change: { status: err.change_status, body: err.change_body },
            error: err.message,
          });
          // VERIFY-THEN-REVERT: a 422 from Shopify isn't always proof the
          // line is still in cart. We've seen cases where /cart/change.js
          // returns 422 (case-pack / min-order rule complaints) but the
          // line was nonetheless removed server-side. Reflexively reverting
          // the checkbox in that case produces the user-reported "uncheck
          // doesn't stick — checkbox auto-rechecks" symptom even though
          // the cart no longer contains the protection.
          //
          // Re-fetch /cart.js, look for the lineKey:
          //   • Still present → genuine failure, revert checkbox.
          //   • Already gone  → server did process it; render the verified
          //                     cart state instead of reverting.
          // Either way, return the verify promise so .finally's
          // setBusy(false) waits for the resolution.
          return fetchCartItems().then(function (verifyCart) {
            if (!verifyCart || !Array.isArray(verifyCart.items)) {
              console.warn('[picker.removePlan] verify returned no cart; reverting checkbox');
              self.revertCheckbox(input);
              self.showPickerError();
              return;
            }
            var lineStillExists = verifyCart.items.some(function (it) { return it.key === lineKey; });
            if (lineStillExists) {
              console.warn('[picker.removePlan] verify confirms line still in cart; reverting checkbox', { lineKey: lineKey });
              self.revertCheckbox(input);
              self.showPickerError();
              return;
            }
            console.warn('[picker.removePlan] verify shows line was actually removed despite error response; applying verified cart', { lineKey: lineKey });
            self.applyCartUpdate(verifyCart, cart);
          }).catch(function (verifyErr) {
            console.error('[picker.removePlan] verify fetch failed', verifyErr);
            if (window.CartOperations) return window.CartOperations.recover();
            self.revertCheckbox(input);
            self.showPickerError();
          });
        })
        .finally(function () {
          self.setBusy(false);
        });
    }

    cartChangeUrl() {
      var base = (typeof routes !== 'undefined' && routes.cart_change_url) ? routes.cart_change_url : '/cart/change';
      return base + '.js';
    }

    findCartHost() {
      return document.querySelector('cart-items') ||
             document.querySelector('cart-drawer') ||
             document.querySelector('cart-notification');
    }

    sectionsForCart(cart) {
      if (!cart || typeof cart.getSectionsToRender !== 'function') return [];
      return cart.getSectionsToRender().map(function (s) { return s.section || s.id; });
    }

    runRequest(url, body, sourceInput) {
      this.setBusy(true);

      var cart = this.findCartHost();
      var sections = this.sectionsForCart(cart);

      var payload = Object.assign({}, body, {
        sections: sections,
        sections_url: window.location.pathname,
      });

      var self = this;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(payload),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok || res.body.status) {
            throw new Error((res.body && (res.body.description || res.body.message)) || 'Cart update failed');
          }
          return self.clearHostDeclinedProperty(res.body, sections).then(function (cartData) {
            self.applyCartUpdate(cartData, cart);
          });
        })
        .catch(function (err) {
          console.error('[cart-protection-picker]', err);
          if (window.CartOperations) return window.CartOperations.recover();
          // Revert ONLY the clicked checkbox. We do NOT reload — Theme
          // Editor blocks iframe redirects. After revert, schedule a
          // /cart.js verify ~400ms later: if the server actually DID
          // process the change (we just misinterpreted the response —
          // e.g. fetch threw on a parsable response or the success body
          // had a `status` field for a non-error reason), the verify
          // will detect the mismatch and re-correct the DOM. This stops
          // the user-reported "uncheck → auto-rechecks back" loop when
          // the AJAX actually succeeded server-side.
          self.revertCheckbox(sourceInput);
          self.showPickerError();
          setTimeout(function () {
            fetchCartItems().then(function (cart) {
              if (!cart || !Array.isArray(cart.items)) return;
              var hostVariantId = self.dataset.hostVariantId || '';
              if (!hostVariantId) return;
              var serverProtectionLine = cart.items.find(function (it) {
                return it.properties && String(it.properties._protection_for_id) === hostVariantId;
              });
              var serverHasProtection = !!serverProtectionLine;
              if (serverHasProtection !== pickerHasSelectedProtection(self)) {
                syncPickerSelection(self, serverProtectionLine || null);
                self.clearPickerError();
              }
            }).catch(function () {});
          }, 400);
        })
        .finally(function () {
          self.setBusy(false);
        });
    }

    clearHostDeclinedProperty(cartData, sections) {
      if (this.dataset.hostDeclined !== 'true') return Promise.resolve(cartData);

      const hostLineKey = this.dataset.hostLineKey || '';
      if (!hostLineKey) {
        console.error('[cart-protection-picker.clearDeclined] missing host line data');
        return Promise.resolve(cartData);
      }

      const self = this;
      let resolvedHostProperties;
      return fetchCartItems()
        .catch(function (err) {
          console.warn('[cart-protection-picker.clearDeclined] current cart fetch failed; using rendered fallback', err);
          return null;
        })
        .then(function (currentCartData) {
          const hostItem = currentCartData && Array.isArray(currentCartData.items)
            ? currentCartData.items.find(function (item) { return item.key === hostLineKey; })
            : null;
          let hostQuantity;

          if (hostItem) {
            hostQuantity = parseInt(hostItem.quantity, 10) || 1;
            resolvedHostProperties = Object.assign({}, hostItem.properties || {});
          } else {
            hostQuantity = parseInt(self.dataset.hostQuantity || '1', 10) || 1;
            try {
              resolvedHostProperties = JSON.parse(self.dataset.hostProperties || '{}');
            } catch (err) {
              throw new Error('Invalid host properties fallback');
            }
          }

          if (!resolvedHostProperties || Array.isArray(resolvedHostProperties)) {
            throw new Error('Missing host properties');
          }

          delete resolvedHostProperties._protection_declined;

          return fetch(self.cartChangeUrl(), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              id: hostLineKey,
              quantity: hostQuantity,
              properties: resolvedHostProperties,
              sections: sections,
              sections_url: window.location.pathname,
            }),
          });
        })
        .then(function (r) {
          return r.json().then(function (body) { return { ok: r.ok, body: body }; });
        })
        .then(function (res) {
          if (!res.ok || res.body.status) {
            throw new Error((res.body && (res.body.description || res.body.message)) || 'Unable to clear declined protection state');
          }
          self.dataset.hostDeclined = 'false';
          self.dataset.hostProperties = JSON.stringify(resolvedHostProperties);
          return res.body;
        })
        .catch(function (err) {
          // The protection add already succeeded. Keep that authoritative
          // cart response so the picker remains removable, then let the
          // Liquid claimant fallback recover the state on the next render.
          console.error('[cart-protection-picker.clearDeclined]', err);
          return cartData;
        });
    }

    applyCartUpdate(data, cart) {
      const errorBox = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
      if (errorBox) errorBox.textContent = '';

      // 0) Capture authoritative truth from response.items BEFORE any DOM
      //    replacement. response.items is the cart's post-write state; we
      //    use it to override any stale section render later.
      var hostVariantId = this.dataset.hostVariantId || '';
      var expectedHasProtection = false;
      var expectedProtectionLine = null;
      if (data && Array.isArray(data.items) && hostVariantId) {
        for (var i = 0; i < data.items.length; i++) {
          var it = data.items[i];
          if (it.properties && String(it.properties._protection_for_id) === hostVariantId) {
            expectedHasProtection = true;
            expectedProtectionLine = it;
            break;
          }
        }
      }

      // 1) Render from AJAX response sections. The /cart/add.js (or
      //    /cart/update.js) response sections HTML reflects the cart at
      //    the time the response was generated — usually fresh, but
      //    Shopify's section render can occasionally lag the cart write
      //    (read-after-write skew). We override this with response.items
      //    in step 1.5 below.
      this.renderSections(cart, data);

      // 1.5) Reconcile DOM with response.items (authoritative). If the
      //     server-rendered sections lagged and show the wrong state for
      //     this host, force-correct by directly toggling checkboxes.
      //     Symptom this prevents: "uncheck → checkbox auto-rechecks back"
      //     when /cart/update.js returns success but its rendered sections
      //     still showed the protection.
      this.reconcilePickers(hostVariantId, expectedHasProtection, expectedProtectionLine);

      // 2) Publish with source 'cart-items' so Dawn's CartItems.onCartUpdate
      //    short-circuits. Why we DON'T let Dawn refetch:
      //
      //    Dawn's onCartUpdate does GET /cart?section_id=main-cart-items
      //    and replaces cart-items.innerHTML. But Shopify has read-after-
      //    write timing where that GET, fired ~100-300ms after our POST,
      //    occasionally returns the cart state from BEFORE our just-added
      //    line is committed. The fresh-fetched HTML then renders the
      //    picker as State B, overwriting the correct State A from step 1.
      //
      //    User-visible symptom: "first few seconds correct, then
      //    auto-unchecks but the price stays in cart total".
      //
      //    Fix: keep step 1's render authoritative. Other subscribers
      //    (cart-quantity-reminder etc.) don't filter by source, so they
      //    still receive the cartData and update.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: data,
        });
      }

      // 3) Drawer / notification — let them handle their own UX (open
      //    drawer, animate, etc.). Wrapped in try-catch since
      //    cart-notification.renderContents needs cartItemKey and can
      //    throw on protection-only line additions.
      if (cart && typeof cart.renderContents === 'function') {
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(document.activeElement);
        try {
          cart.renderContents(data);
        } catch (e) {
          console.debug('[cart-protection-picker] cart.renderContents skipped:', e);
        }
        this.reconcilePickers(hostVariantId, expectedHasProtection, expectedProtectionLine);
      }

      // No post-add cleanup runs here. Orphan/duplicate detection happens
      // in two non-racing contexts: addPlan's pre-clean (before the add)
      // and cleanupOnPageLoad (page navigation, no recent write to race
      // against). cart-protection-sync handles host-removal cleanup.
    }

    reconcilePickers(hostVariantId, expectedHasProtection, expectedProtectionLine) {
      if (!hostVariantId) return;

      document.querySelectorAll(
        `cart-protection-picker[data-host-variant-id="${hostVariantId}"][data-protection-claimant="true"]`
      ).forEach(function (picker) {
        const renderedHasProtection = pickerHasSelectedProtection(picker);
        const renderedPlan = picker.querySelector(
          '.cart-protection-picker__checkbox[data-action="add"]:checked'
        );
        const expectedVariantId = expectedProtectionLine && expectedProtectionLine.variant_id
          ? String(expectedProtectionLine.variant_id)
          : '';
        const renderedVariantMismatch = renderedPlan && renderedPlan.dataset.variantId !== expectedVariantId;

        if (renderedHasProtection !== expectedHasProtection || renderedVariantMismatch) {
          syncPickerSelection(picker, expectedProtectionLine);
        }
      });
    }

    renderSections(cart, data) {
      if (window.CartOperations) { window.CartOperations.render(data); return; }
      if (!cart || typeof cart.getSectionsToRender !== 'function' || !data || !data.sections) return;

      cart.getSectionsToRender().forEach(function (section) {
        var sectionName = section.section || section.id;
        var html = data.sections[sectionName];
        if (!html) return;
        const container = document.getElementById(section.id) ||
          (section.selector ? document.querySelector(section.selector) : null);
        if (!container) return;

        var selector = section.selector || '.shopify-section';
        var newDoc;
        try {
          newDoc = new DOMParser().parseFromString(html, 'text/html');
        } catch (e) {
          return;
        }
        var newNode = newDoc.querySelector(selector);
        if (!newNode) return;

        var target = container.querySelector(selector) || container;
        target.innerHTML = newNode.innerHTML;
      });
    }

    setBusy(isBusy) {
      this.busy = isBusy;
      this.classList.toggle('is-busy', isBusy);
      this.querySelectorAll('.cart-protection-picker__checkbox').forEach(function (cb) {
        cb.disabled = isBusy;
      });
    }

    cartAddUrl() {
      var base = (typeof routes !== 'undefined' && routes.cart_add_url) ? routes.cart_add_url : '/cart/add';
      return base + '.js';
    }

    cartUpdateUrl() {
      var base = (typeof routes !== 'undefined' && routes.cart_update_url) ? routes.cart_update_url : '/cart/update';
      return base + '.js';
    }
  }

  customElements.define('cart-protection-picker', CartProtectionPicker);
})();
